import React, { useState } from 'react';
import {
  Upload,
  CheckCircle,
  RefreshCw,
  Play,
  Warehouse,
  FileSpreadsheet
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, ref, uploadBytesResumable, uploadString } from 'firebase/storage';

const FILE_TYPES = [
  { key: 'warehouse_billing', label: 'Warehouse Billing', table: 'warehouse_billing', icon: FileSpreadsheet, color: 'amber', accept: '.xlsx,.xls,.csv' },
];

export default function WarehouseBillingWorkflow({ tenantId } = {}) {
  const [uploads, setUploads] = useState({});

  const updateUpload = (key, patch) => {
    setUploads(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const handleFileSelect = (key, e) => {
    const file = e.target.files?.[0];
    if (file) {
      updateUpload(key, { file, result: null, error: null });
    }
  };

  const uploadFile = async (fileType) => {
    const state = uploads[fileType.key];
    if (!state?.file) return;

    updateUpload(fileType.key, { uploading: true, progress: 0, error: null });

    try {
      const storage = getStorage();
      const isCSV = state.file.name.toLowerCase().endsWith('.csv');
      const ext = state.file.name.split('.').pop().toLowerCase();
      const storagePath = `inmocean-raw-data/warehouse/${fileType.table}.${ext}`;
      const storageRef = ref(storage, storagePath);

      if (isCSV) {
        updateUpload(fileType.key, { progress: 10 });
        const rawText = await state.file.text();
        updateUpload(fileType.key, { progress: 20 });
        await uploadString(storageRef, rawText, 'raw', { contentType: 'text/csv' });
      } else {
        updateUpload(fileType.key, { progress: 10 });
        await new Promise((resolve, reject) => {
          const uploadTask = uploadBytesResumable(storageRef, state.file);
          uploadTask.on('state_changed',
            (snapshot) => {
              const progress = 10 + Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 40);
              updateUpload(fileType.key, { progress });
            },
            (error) => reject(error),
            () => resolve()
          );
        });
      }

      updateUpload(fileType.key, { progress: 55 });

      const functions = getFunctions();
      const processBIUpload = httpsCallable(functions, 'processBIUpload', { timeout: 600000 });

      updateUpload(fileType.key, { progress: 60 });
      const result = await processBIUpload({
        bucketName: 'actualizer2.firebasestorage.app',
        filePath: storagePath,
        tableName: fileType.table,
        datasetName: 'inmocean_raw',
        tenantId
      });

      updateUpload(fileType.key, {
        progress: 100,
        uploading: false,
        result: {
          success: true,
          filename: state.file.name,
          rowCount: result.data?.rowsInserted || result.data?.rowCount || 0,
        }
      });
    } catch (err) {
      updateUpload(fileType.key, { error: err.message, uploading: false });
    }
  };

  const resetAll = () => setUploads({});

  const completedCount = FILE_TYPES.filter(ft => uploads[ft.key]?.result).length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-200 p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-xl">
              <Warehouse className="h-7 w-7 text-amber-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Warehouse Billing</h1>
              <p className="text-gray-600 text-sm">Upload warehouse billing files</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">{completedCount}/{FILE_TYPES.length} uploaded</span>
            <button
              onClick={resetAll}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg text-sm"
            >
              <RefreshCw className="h-4 w-4" />
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Upload Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {FILE_TYPES.map((ft, idx) => {
          const state = uploads[ft.key] || {};
          const Icon = ft.icon;
          const isCompleted = !!state.result;
          const borderClass = isCompleted
            ? 'border-green-300 bg-green-50'
            : 'border-gray-200 hover:border-amber-200';

          return (
            <div key={ft.key} className={`bg-white rounded-lg border-2 ${borderClass} p-4`}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                  isCompleted ? 'bg-green-500 text-white' : 'bg-amber-100 text-amber-600'
                }`}>
                  {isCompleted ? <CheckCircle className="w-3.5 h-3.5" /> : idx + 1}
                </div>
                <Icon className={`h-5 w-5 ${isCompleted ? 'text-green-600' : 'text-amber-500'}`} />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-semibold text-gray-800 block truncate">{ft.label}</span>
                  <span className="text-[10px] text-gray-400 font-mono">{ft.table}</span>
                </div>
              </div>

              <div className="text-xs">
                {!state.result ? (
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept={ft.accept || '.csv'}
                      onChange={(e) => handleFileSelect(ft.key, e)}
                      className="hidden"
                      id={`wh-file-${ft.key}`}
                    />
                    <label
                      htmlFor={`wh-file-${ft.key}`}
                      className="flex items-center justify-center gap-1 px-3 py-2 bg-amber-50 text-amber-700 rounded hover:bg-amber-100 cursor-pointer border border-amber-200"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      {state.file ? state.file.name.substring(0, 25) + (state.file.name.length > 25 ? '...' : '') : 'Select CSV'}
                    </label>
                    {state.file && (
                      <button
                        onClick={() => uploadFile(ft)}
                        disabled={state.uploading}
                        className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
                      >
                        {state.uploading
                          ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> {state.progress || 0}%</>
                          : <><Play className="h-3.5 w-3.5" /> Upload</>
                        }
                      </button>
                    )}
                    {state.error && (
                      <div className="text-red-500 truncate text-[11px]">{state.error}</div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="text-green-600 font-medium">
                      <CheckCircle className="h-3.5 w-3.5 inline mr-1" />
                      {state.result.rowCount?.toLocaleString()} rows uploaded
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
